<script setup>
import { ref } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";

const props = defineProps({
  selectedPlan: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["back", "subscription-result"]);

const subscriptionStore = useSubscriptionStore();
const isProcessing = ref(false);

/**
 * Confirma o downgrade, cria a nova assinatura "free"
 * e emite um resultado de sucesso para o pai (UpgradePage.vue).
 */
async function confirmDowngrade() {
  if (!props.selectedPlan) {
    emit("subscription-result", {
      success: false,
      message: "Erro: Nenhum plano selecionado.",
    });
    return;
  }
  
  isProcessing.value = true;

  try {
    // 1. Prepara o payload para a nova assinatura "Free"
    const payload = {
      planId: props.selectedPlan.planId,
      paymentMethod: "free",
      transactionId: "DOWGRADE_N/A", // Identificador
      trialDays: 0,
    };

    // 2. Chama a action do store (assumindo que o backend
    // cancela a assinatura paga anterior ao criar esta)
    const newSubscription = await subscriptionStore.createSubscription(payload);

    // 3. Emite um resultado de SUCESSO.
    // O 'handleSubscriptionResult' no pai vai pegar isso.
    // Para forçar a exibição do 'SubscriptionResult.vue' (Step 4),
    // enviamos um objeto que se parece com um pagamento bem-sucedido.
    emit("subscription-result", {
      success: true,
      status: "authorized", // Faz o pai entender como sucesso (vai para o Step 4)
      subscriptionId: newSubscription?.subscriptionId || 0,
      message: "Seu plano foi alterado para Gratuito com sucesso.",
    });

  } catch (error) {
    console.error("Falha ao fazer downgrade:", error);
    // 4. Emite um resultado de FALHA
    emit("subscription-result", {
      success: false,
      status: "rejected",
      message: error.message || "Não foi possível alterar seu plano. Tente novamente.",
    });
  } finally {
    isProcessing.value = false;
  }
}
</script>

<template>
  <div>
    <div 
      v-if="isProcessing" 
      class="text-center p-8"
    >
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
      <p class="text-lg text-gray-700">Alterando seu plano...</p>
    </div>

    <div v-else>
      <h2 class="text-3xl font-semibold text-[color:var(--text-dark)]">Confirmar Alteração</h2>
      <p class="mt-2 text-base text-[color:var(--text-light)]">
        Você está prestes a mudar seu plano para o
        <strong class="font-semibold">{{ selectedPlan.name }}</strong>.
      </p>
      <p class="mt-4 text-sm text-gray-500">
        Ao confirmar, sua assinatura paga atual será cancelada (de acordo com as regras do seu backend) e você passará a usar os limites do plano gratuito.
      </p>

      <div class="mt-8 space-y-4">
        <button
          @click="confirmDowngrade"
          type="button"
          class="w-full inline-flex items-center justify-center rounded-lg px-6 py-3 text-base font-semibold bg-[var(--bg-sidebar-left)] text-[color:var(--text-dark)] transition hover:opacity-90"
        >
          Confirmar e Mudar para Gratuito
        </button>
        <button
          @click="$emit('back')"
          type="button"
          class="w-full inline-flex items-center justify-center rounded-lg px-6 py-3 text-base font-semibold bg-surface text-[color:var(--text-light)] border border-[color:var(--border-color)] shadow-sm transition hover:bg-gray-50"
        >
          Voltar (Manter Plano Atual)
        </button>
      </div>
    </div>
  </div>
</template>